import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import ChatInterface from "./Components/ChatInterface";
import MyProgress from "./Components/MyProgress";
import VideoUpload from "./Components/VideoUpload";
// import FidelityScore from "./Components/FidelityScore";
import TranscriptionWindow from "./Components/TranscriptionWindow";
import ProcessDiagram from "./Components/ProcessDiagram";
import AIAnalysis from "./Components/AIAnalysis";
import HighlightsTimeline from "./Components/HighlightsTimeline";
import Achievements from "../Achievements";
import { io } from "socket.io-client";
import "./HomePage.css";
import axios from "axios";
import {
  getCachedSessions,
  getCachedUserStats,
  getCachedRestorePayload,
  setCachedSessions,
  setCachedRestorePayload,
  setCachedUserStats,
} from "../../offline/sessionOffline";

const REACT_APP_API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const LEFT_COLUMN_MIN_PERCENT = 48;
const LEFT_COLUMN_MAX_PERCENT = 78;
const CHAT_PANEL_MIN_HEIGHT = 320;
const CHAT_PANEL_MAX_HEIGHT = 760;

const HomePage = ({ userId, onAppReload, profile, isGuest, isOnline = true, refreshTrigger, setIsDemo, isDemo, restoreRequest }) => {
  const [videoTime, setVideoTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [newVideoUploaded, setNewVideoUploaded] = useState(false);
  const [videoId, setVideoId] = useState(null);
  const [analysisResults, setAnalysisResults] = useState([]);
  const [showTranscription, setShowTranscription] = useState(false);  
  const [restoredChatMessages, setRestoredChatMessages] = useState([]);
  const [restoredTranscriptions, setRestoredTranscriptions] = useState([]);
  const [hasRespondedToIntro, setHasRespondedToIntro] = useState(false);
  const [slpConversations, setSlpConversations] = useState({});
  const [analysisReady, setAnalysisReady] = useState(false);
  const [video, setVideo] = useState(null);
  const [videoMeta, setVideoMeta] = useState({ width: 0, height: 0 });
  const [videoFeedbackOffset, setVideoFeedbackOffset] = useState(0);
  const [videoFeedbackHeight, setVideoFeedbackHeight] = useState(0);
  const [timeSpentMinutes, setTimeSpentMinutes] = useState(0);
  const [messagesSent, setMessagesSent] = useState(0);
  const [streakDays, setStreakDays] = useState(0);
  const [totalSessions, setTotalSessions] = useState(0);
  const [leftColumnWidth, setLeftColumnWidth] = useState(66);
  const [leftTopHeight, setLeftTopHeight] = useState(520);
  const [dragState, setDragState] = useState(null);
  const layoutRef = useRef(null);
  const leftColumnRef = useRef(null);
  const videoPanelRef = useRef(null);
  const videoStageRef = useRef(null);
  const videoSupplementRef = useRef(null);
  const hasRestoredFromUrl = useRef(false);
  const visibleAnalysisResults = useMemo(
    () => analysisResults.filter((r) => r.is_deleted !== true),
    [analysisResults]
  );

  const handleVideoTimeUpdate = (time, duration) => {
    setVideoTime(time);
    setVideoDuration(duration);
  };

  const handleVideoMetadata = useCallback((meta) => {
    setVideoMeta({
      width: Number(meta?.width || 0),
      height: Number(meta?.height || 0),
    });
  }, []);

  const seekToTime = (time) => {
    const video = document.querySelector("video");
    if (video) {
      video.currentTime = time;
    }
  };

  useEffect(() => {
    if (!userId) return;

    const cachedSessions = getCachedSessions(userId);
    if (cachedSessions?.sessions) {
      setTotalSessions(cachedSessions.sessions.length || 0);
    }

    const cachedStats = getCachedUserStats(userId);
    if (cachedStats?.stats) {
      setMessagesSent(cachedStats.stats.messagesSent || 0);
      setTimeSpentMinutes(cachedStats.stats.timeSpentMinutes || 0);
      setStreakDays(cachedStats.stats.streakDays || 0);
      if (typeof cachedStats.stats.totalSessions === "number") {
        setTotalSessions(cachedStats.stats.totalSessions);
      }
    }

    axios
      .get(`${REACT_APP_API_BASE_URL}/api/sessions/${userId}`)
      .then((res) => {
        const { sessions } = res.data;
        setTotalSessions(sessions.length);
        setCachedSessions(userId, sessions);
      })
      .catch((err) => console.error("Error fetching sessions:", err));

    axios
      .get(`${REACT_APP_API_BASE_URL}/api/user-stats/${userId}`)
      .then((res) => {
        const nextStats = {
          messagesSent: res.data.messagesSent || 0,
          timeSpentMinutes: res.data.timeSpentMinutes || 0,
          streakDays: res.data.streakDays || 0,
          totalSessions,
        };
        setMessagesSent(nextStats.messagesSent);
        setTimeSpentMinutes(nextStats.timeSpentMinutes);
        setStreakDays(nextStats.streakDays);
        setCachedUserStats(userId, nextStats);
      })
      .catch((err) => console.error("Error fetching user stats:", err));
  }, [totalSessions, userId]);

  useEffect(() => {
    if (!videoId) return;

    const socket = io(REACT_APP_API_BASE_URL);

    socket.on("analysis_progress", (data) => {
      if (data.videoId !== videoId) return;
      setAnalysisResults((prevResults) => [...prevResults, data]);
      if ((isDemo || isGuest) && !analysisReady) setAnalysisReady(true);
    });

    socket.on("analysis_ready", ({ videoId: id }) => {
      if (id === videoId) setAnalysisReady(true);
    });

    return () => {
      socket.disconnect();
    };
  }, [videoId, analysisReady, isDemo, isGuest]);

  const handleSessionRestore = useCallback(async (session) => {
    const targetId = session?.id;
    if (!targetId) return;
    const applyRestoreData = (data) => {
      setHasRespondedToIntro(true);
      setAnalysisReady(Boolean(data.session?.analysis_ready));
      setVideoId(targetId);
      const mappedSegments = (data.analysisSegments || []).map(seg => ({
        "Begin-End": seg.begin_end,
        "Fidelity Score": seg.fidelity_score,
        "Feedback": seg.ai_reasoning,
        "Strategy": seg.strategy,
        "Child Response": seg.child_response,
        "Child Transcript": seg.all_sentences,
        is_deleted: seg.is_deleted,
      }));
      setAnalysisResults(mappedSegments);
      const aiMessages = [];
      const slpConvoMap = {};

      (data.chatHistory || []).forEach(msg => {
        const base = {
          text: msg.message,
          sender:
            msg.role === "assistant" ? "bot"
            : msg.role === "slp"      ? "slp"
            : msg.role === "user-slp" ? "user"
            : "user",
        };

        if (msg.quick_replies) {
          base.options = msg.quick_replies.map(label => ({
            label,
            value: label,
          }));
        }

        if (msg.slp_id) {
          if (!slpConvoMap[msg.slp_id]) slpConvoMap[msg.slp_id] = [];
          slpConvoMap[msg.slp_id].push(base);
        } else {
          aiMessages.push(base);
        }
      });

      setRestoredChatMessages(aiMessages);
      setSlpConversations(slpConvoMap);
      setRestoredTranscriptions(
        (data.transcriptions || []).map(t => ({
          start: t.start_time,
          end: t.end_time,
          text: t.text,
          speaker: t.speaker,
        }))
      );

      if (data.session?.is_demo) {
        setVideo(process.env.PUBLIC_URL + "/demo/demo.MOV");
      } else {
        setVideo(null);
      }
    };

    if (!isOnline) {
      const cached = getCachedRestorePayload(targetId);
      if (cached?.payload) {
        applyRestoreData(cached.payload);
      }
      return;
    }

    try {
      const res = await fetch(`${REACT_APP_API_BASE_URL}/api/restore-session/${targetId}`);
      if (!res.ok) throw new Error("Failed to restore");
      const data = await res.json();
      setCachedRestorePayload(targetId, data);
      applyRestoreData(data);
      if (!data.session?.is_demo && data.objectKey) {
        const safeKey = encodeURIComponent(data.objectKey);
        setVideo(`${REACT_APP_API_BASE_URL}/api/video/${safeKey}`);
      }
    } catch (err) {
      console.error("Error restoring session:", err);
      const cached = getCachedRestorePayload(targetId);
      if (cached?.payload) {
        applyRestoreData(cached.payload);
      }
    }
  }, [isOnline]);

  useEffect(() => {
    if (!restoreRequest?.id) return;
    handleSessionRestore({ id: restoreRequest.id });
  }, [restoreRequest, handleSessionRestore]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const restoreId = params.get("restore");
    if (!restoreId || hasRestoredFromUrl.current) return;
    hasRestoredFromUrl.current = true;
    handleSessionRestore({ id: restoreId });
    window.history.replaceState({}, "", "/Project-Demo");
  }, [handleSessionRestore]);

  const handleTranscriptionToggle = () => {
    setShowTranscription((prev) => !prev);
  };

  const startDrag = (type, event) => {
    event.preventDefault();
    setDragState({
      type,
      startX: event.clientX,
      startY: event.clientY,
      startLeftWidth: leftColumnWidth,
      startLeftTopPx: leftTopHeight,
    });
  };

  useEffect(() => {
    if (!dragState) return;

    const onMouseMove = (event) => {
      if (dragState.type === "column") {
        const layoutWidth = layoutRef.current?.getBoundingClientRect().width || 0;
        if (!layoutWidth) return;
        const deltaPercent = ((event.clientX - dragState.startX) / layoutWidth) * 100;
        setLeftColumnWidth(
          clamp(
            dragState.startLeftWidth + deltaPercent,
            LEFT_COLUMN_MIN_PERCENT,
            LEFT_COLUMN_MAX_PERCENT
          )
        );
        return;
      }

      if (dragState.type === "left-row") {
        const deltaY = event.clientY - dragState.startY;
        setLeftTopHeight(
          clamp(
            dragState.startLeftTopPx + deltaY,
            CHAT_PANEL_MIN_HEIGHT,
            CHAT_PANEL_MAX_HEIGHT
          )
        );
      }
    };

    const onMouseUp = () => {
      setDragState(null);
    };

    const isColumnDrag = dragState.type === "column";
    document.body.classList.add("home-is-resizing");
    document.body.classList.toggle("home-is-resizing-column", isColumnDrag);
    document.body.classList.toggle("home-is-resizing-row", !isColumnDrag);

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      document.body.classList.remove("home-is-resizing");
      document.body.classList.remove("home-is-resizing-column");
      document.body.classList.remove("home-is-resizing-row");
    };
  }, [dragState]);

  const isPortraitVideo =
    videoMeta.height > 0 &&
    videoMeta.width > 0 &&
    videoMeta.height / videoMeta.width > 1.1;

  const resolvedVideoLayout = isPortraitVideo ? "side" : "stacked";

  useEffect(() => {
    if (!videoPanelRef.current || resolvedVideoLayout !== "side") {
      setVideoFeedbackOffset(0);
      return undefined;
    }

    const panel = videoPanelRef.current;
    const header = panel.querySelector(".training-video-header");
    const divider = panel.querySelector(".orange-divider");
    if (!header) {
      setVideoFeedbackOffset(0);
      return undefined;
    }

    const updateOffset = () => {
      const headerHeight = header.getBoundingClientRect().height || 0;
      const dividerHeight = divider?.getBoundingClientRect().height || 0;
      // Include small spacing before video area starts.
      setVideoFeedbackOffset(Math.ceil(headerHeight + dividerHeight + 14));
    };

    updateOffset();
    const observer = new ResizeObserver(updateOffset);
    observer.observe(header);
    if (divider) observer.observe(divider);
    window.addEventListener("resize", updateOffset);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateOffset);
    };
  }, [resolvedVideoLayout, video]);

  useEffect(() => {
    if (resolvedVideoLayout !== "side") {
      setVideoFeedbackHeight(0);
      return undefined;
    }

    const panelEl = videoPanelRef.current;
    const stageEl = videoStageRef.current;
    const supplementEl = videoSupplementRef.current;
    if (!panelEl || !stageEl) {
      setVideoFeedbackHeight(0);
      return undefined;
    }

    const updateHeight = () => {
      const panelRect = panelEl.getBoundingClientRect();
      const stageRect = stageEl.getBoundingClientRect();
      const supplementRect = supplementEl?.getBoundingClientRect();
      const feedbackTop = panelRect.top + videoFeedbackOffset;
      const leftBottom = Math.max(
        stageRect.bottom || 0,
        supplementRect?.bottom || 0
      );
      const computed = Math.max(
        300,
        Math.round(leftBottom - feedbackTop)
      );
      setVideoFeedbackHeight(computed);
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(panelEl);
    observer.observe(stageEl);
    if (supplementEl) observer.observe(supplementEl);
    window.addEventListener("resize", updateHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateHeight);
    };
  }, [resolvedVideoLayout, videoFeedbackOffset, video, analysisReady, showTranscription]);

  return (
    <div className="homepage-container">
      <div className="homepage-resizable-layout" ref={layoutRef}>
        <div
          className="resizable-column resizable-column-left"
          ref={leftColumnRef}
          style={{ flexBasis: `${leftColumnWidth}%` }}
        >
          <section
            className="home-panel home-panel-chat"
            style={{ flex: `0 0 ${leftTopHeight}px` }}
          >
            <div className="home-panel-content home-panel-content-chat">
              <ChatInterface
                videoTime={videoTime}
                videoDuration={videoDuration}
                newVideoUploaded={newVideoUploaded}
                setNewVideoUploaded={setNewVideoUploaded}
                seekToTime={seekToTime}
                videoId={videoId}
                profile={profile}
                isOnline={isOnline}
                isGuest={isGuest}
                refreshTrigger={refreshTrigger}
                restoredChatMessages={restoredChatMessages}
                hasRespondedToIntro={hasRespondedToIntro}
                setHasRespondedToIntro={setHasRespondedToIntro}
                slpConversations={slpConversations}
                setSlpConversations={setSlpConversations}
              />
            </div>
          </section>

          <div
            className="panel-splitter panel-splitter-horizontal"
            onMouseDown={(event) => startDrag("left-row", event)}
            aria-label="Resize left panels"
            role="separator"
          />

          <section
            className="home-panel home-panel-video"
            style={{ flex: "0 0 auto" }}
            ref={videoPanelRef}
          >
            <div
              className={`home-panel-content home-panel-content-video home-video-layout-${resolvedVideoLayout}`}
              style={{
                "--video-feedback-offset": `${videoFeedbackOffset}px`,
                "--video-feedback-height": `${videoFeedbackHeight}px`,
              }}
            >
              <div className="video-stage" ref={videoStageRef}>
                <VideoUpload
                  onAppReload={onAppReload}
                  onVideoTimeUpdate={handleVideoTimeUpdate}
                  onVideoMetadata={handleVideoMetadata}
                  setNewVideoUploaded={setNewVideoUploaded}
                  setVideoId={setVideoId}
                  videoId={videoId}
                  videoDuration={videoDuration}
                  seekToTime={seekToTime}
                  handleTranscription={handleTranscriptionToggle}
                  profile={profile}
                  video={video}
                  setVideo={setVideo}
                  setIsDemo={setIsDemo}
                />
              </div>
              <div className="video-side-supplement" ref={videoSupplementRef}>
                {videoId && analysisReady && visibleAnalysisResults.length > 0 && (
                  <HighlightsTimeline
                    videoId={videoId}
                    videoDuration={videoDuration}
                    seekToTime={seekToTime}
                    analysisResults={visibleAnalysisResults}
                  />
                )}
                <div
                  className={`transcription-container ${
                    showTranscription ? "visible" : ""
                  }`}
                >
                  {showTranscription && (
                    <TranscriptionWindow
                      videoId={videoId}
                      videoTime={videoTime}
                      seekToTime={seekToTime}
                      restoredTranscriptions={restoredTranscriptions}
                    />
                  )}
                </div>
              </div>
              <div className="video-feedback-stack">
                {videoId && (
                  <AIAnalysis
                    analysisResults={visibleAnalysisResults}
                    videoId={videoId}
                    seekToTime={seekToTime}
                    analysisReady={analysisReady}
                    isDemo={isDemo}
                    isGuest={isGuest}
                  />
                )}
              </div>
            </div>
          </section>
        </div>

        <div
          className="panel-splitter panel-splitter-vertical"
          onMouseDown={(event) => startDrag("column", event)}
          aria-label="Resize columns"
          role="separator"
        />

        <div className="resizable-column resizable-column-right">
          <section
            className="home-panel home-panel-progress"
            style={{ flex: "0 0 480px" }}
          >
            <div className="home-panel-content home-panel-content-progress">
              <div className="progress-stack">
                <div className="progress-card-wrapper">
                  <MyProgress
                    userId={userId}
                    totalSessions={totalSessions}
                    timeSpent={timeSpentMinutes}
                    messagesSent={messagesSent}
                    streakDays={streakDays}
                    headerAddon={
                      <Achievements
                        stats={{
                          videos: totalSessions,
                          minutes: timeSpentMinutes,
                          messages: messagesSent,
                          streak: streakDays,
                        }}
                      />
                    }
                  />
                </div>
              </div>
            </div>
          </section>

          <section
            className="home-panel home-panel-about"
            style={{ flex: "0 0 auto" }}
          >
            <div className="home-panel-content home-panel-content-about">
              <ProcessDiagram videoTime={videoTime} />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
